import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { ScaledGrid, type LayoutEditing } from '@/pages/home/ScaledGrid';
import { GAP, hasCustomLayout, ROW_UNIT, type PlacedWidget } from '@/pages/home/widgetLayout';
import { WidgetBoundary } from '@/pages/home/widgets/WidgetFrame';

/**
 * 홈 위젯을 <b>메이슨리</b>로 쌓는다 — 위젯마다 높이가 다르면 다른 대로 놓이게.
 *
 * 예전에는 평범한 CSS 그리드였다. 그러면 한 줄의 모든 칸이 그 줄에서 가장 큰 위젯의 바닥에
 * 맞춰 정렬돼(선반처럼) 짧은 위젯 아래에 빈 공간이 생기고, 대시보드가 표처럼 뻣뻣해 보였다.
 *
 * ## 어떻게 하나 — 그리드 행 스팬
 *
 * `column-count`(CSS 멀티컬럼) 방식은 순서가 열 단위(위→아래, 그다음 열)로 바뀌어 사용자가 정한
 * 좌→우 순서가 깨지고 wide 위젯도 못 싣는다. 그래서 그리드를 촘촘한 행(`gridAutoRows`)으로 깔고
 * 각 위젯이 자기 내용 높이만큼 <b>행을 몇 칸 차지할지</b>를 재서 정한다. 좌→우→아래 순서가 그대로
 * 남고 wide(2칸)도 그대로 된다.
 *
 * 위젯은 자기 데이터가 늦게 도착하며 키가 자란다. 그래서 각 칸을 {@link ResizeObserver} 로 지켜보다
 * 높이가 바뀌면 스팬을 다시 잡는다.
 *
 * ## 격자가 둘이다
 *
 *  · <b>기본</b> — 브레이크포인트로 열 수가 바뀐다(모바일 1 · md 2 · 2xl 3). 배치 커스텀을 한 번도
 *    안 한 사람의 홈이다.
 *  · <b>좌표</b> — 사용자가 「배치 커스텀」 으로 자리와 크기를 못 박은 경우({@link widgetLayout}).
 *    위젯마다 (열, 행) 좌표를 갖고 <b>아무도 안 건드리면 아무도 안 움직인다</b>.
 *
 * 편집 화면을 따로 만들지 않고 <b>같은 격자에 손잡이만 얹는다</b>(`editing`). 편집용 미리보기를
 * 따로 그리면 저장한 뒤의 홈과 한 픽셀씩 어긋나고, 그 어긋남은 늘 저장한 뒤에 발견된다.
 */

export type { LayoutEditing };

export function WidgetMasonry({ items, editing }: { items: PlacedWidget[]; editing?: LayoutEditing }) {
  if (editing || hasCustomLayout(items)) return <ScaledGrid items={items} editing={editing} />;

  return (
    // grid-cols-1 을 빼면 안 된다. 모바일에서 열 정의가 없으면 암시적 열이 `auto` 로 잡혀
    // 가장 넓은 위젯(가로 스크롤 띠·nowrap 표)의 최대 콘텐츠 너비까지 늘어난다 — 화면이
    // 통째로 옆으로 밀린다. minmax(0,1fr) 로 못 박아야 위젯이 화면 안에서 접힌다.
    //
    // <b>1열은 모바일뿐이다</b>(사용자 지시). 아이패드·노트북·넓은 웹은 최소 두 칸이 나란히
    // 서야 대시보드로 읽힌다 — 한 칸씩 세로로 흐르면 아래 위젯은 스크롤해야만 보인다.
    //
    // 그래서 2열은 md(768)부터다. 이 폭에서 위젯이 눌리지 않는 것은 사이드바가 같은 지점에서
    // 서랍으로 물러나기 때문이다({@link AppLayout}) — 사이드바가 자리를 차지한 채 2열로 가르면
    // 위젯 하나가 248px 까지 눌려 할일 한 줄에 글자가 서너 자만 남는다. 둘은 <b>같이 움직여야 한다</b>.
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 2xl:grid-cols-3" style={{ gridAutoRows: `${ROW_UNIT}px` }}>
      {items.map(({ definition, size }) => (
        // 넓은 위젯의 span 은 열 수와 <b>같은 지점</b>에서 켜져야 한다. 1열 그리드에 span 2 가 남으면
        // 암시적 열이 하나 더 생겨 위젯이 화면 밖으로 밀린다.
        // `data-tour` 는 안내가 <b>위젯 한 칸을 짚을</b> 때 쓰는 이름표다(`widget-news` …).
        // 위젯마다 손으로 달지 않고 여기서 한 번에 준다 — 새 위젯이 생겨도 빠뜨릴 자리가 없다.
        <MasonryItem
          key={definition.widgetKey}
          tourKey={`widget-${definition.widgetKey}`}
          className={size === 'wide' ? 'md:col-span-2' : undefined}
        >
          <WidgetBoundary title={definition.title}>
            <definition.Component />
          </WidgetBoundary>
        </MasonryItem>
      ))}
    </div>
  );
}

/**
 * 위젯 한 칸. 자기 높이를 재서 <b>행을 몇 칸 쓸지</b>를 정한다.
 *
 * <h3>덜 재면 위젯이 겹쳐 보인다</h3>
 * 행 높이가 8px 로 고정돼 있어 <b>자리는 스팬이 정한다</b>. 카드가 그 자리보다 크면 잘리지 않고
 * 그대로 흘러넘쳐 아래 위젯 위에 겹쳐 그려진다 — 겹침은 그리드가 깨진 것이 아니라 "높이를 덜
 * 잡았다" 는 신호다. 그래서 재는 시점을 넉넉히 둔다:
 *
 *  · 렌더할 때마다 (자식이 바뀌면 높이도 바뀐다)
 *  · {@link ResizeObserver} — 데이터가 늦게 도착해 키가 자랄 때
 *  · 창 크기 변경 — 열 수가 바뀌면 같은 내용도 높이가 달라진다
 *  · 웹폰트 적용 완료 — 폰트가 바뀌면 줄 수가 바뀐다(Pretendard 를 늦게 받는 첫 방문)
 */
function MasonryItem({
  className,
  tourKey,
  children,
}: {
  className?: string;
  tourKey?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [span, setSpan] = useState(1);

  // 렌더마다 다시 잰다. 자식이 바뀌어 키가 달라진 경우는 ResizeObserver 가 알려주기 전에 이미
  // 화면에 그려지므로, 여기서 같은 프레임에 바로잡아야 겹친 상태가 눈에 띄지 않는다.
  useLayoutEffect(() => {
    const wrapper = ref.current;
    if (!wrapper) return;

    const measure = () => {
      const content = wrapper.firstElementChild;
      // scrollHeight 는 넘쳐 나온 부분까지 센다 — 자리를 덜 잡은 상태에서도 진짜 높이가 나온다.
      const height = content ? content.getBoundingClientRect().height : wrapper.scrollHeight;
      // 아이템이 차지할 행 수. +GAP / (ROW_UNIT+GAP) 는 스팬 사이에 끼는 간격까지 셈에 넣어,
      // 실제 높이에 간격 하나를 더한 만큼만 자리를 잡게 한다.
      setSpan(Math.max(1, Math.ceil((height + GAP) / (ROW_UNIT + GAP))));
    };
    measure();

    window.addEventListener('resize', measure);
    // 폰트는 레이아웃이 끝난 뒤에 바뀔 수 있다. 그때 다시 재지 않으면 첫 화면만 겹쳐 보인다.
    document.fonts?.ready.then(measure).catch(() => {});

    // 테스트 환경(jsdom)에는 ResizeObserver 가 없다 — 없으면 위의 두 계기로만 잰다.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer && wrapper.firstElementChild) observer.observe(wrapper.firstElementChild);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  });

  return (
    // min-w-0 — 그리드 항목의 기본 최소 너비는 `auto` 라, 열이 1fr 이어도 내용이 넓으면 칸을 뚫는다.
    <div
      ref={ref}
      data-tour={tourKey}
      className={`min-w-0 ${className ?? ''}`}
      style={{ gridRowEnd: `span ${span}` }}
    >
      {children}
    </div>
  );
}

